import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Group, GroupSchedule } from '@/api/groups'
import { intlLocale } from '@/i18n'

// 2024-01-01 is a Monday (ISO day 1) — a fixed, known reference used only to
// hand `Intl.DateTimeFormat` a date landing on each weekday; never read as a
// real date elsewhere.
const ISO_WEEKDAY_REFERENCE = new Date(2024, 0, 1)

/**
 * Shared display formatting for groups — used by both the Groups table/
 * mobile-row cells and the group detail card/mobile screen, so the two
 * never drift on how a name, schedule slot, or recurrence type reads.
 */
export function useGroupDisplay() {
  const { t } = useI18n()

  // Short weekday names via Intl (matches BookingScheduleView's own
  // `dayFmt`) instead of static translated strings, so this follows the
  // viewer's locale automatically rather than needing its own i18n keys.
  const dowFmt = computed(() => new Intl.DateTimeFormat(intlLocale.value, { weekday: 'short' }))
  const dowLabels = computed<string[]>(() => {
    const labels = ['']
    for (let isoDay = 1; isoDay <= 7; isoDay++) {
      const date = new Date(ISO_WEEKDAY_REFERENCE)
      date.setDate(date.getDate() + (isoDay - 1))
      labels.push(dowFmt.value.format(date))
    }
    return labels
  })

  /** Display name — falls back to the description, then a placeholder, matching `BookingType`'s nullable-name convention. */
  function displayName(group: Pick<Group, 'name' | 'description'>): string {
    return group.name ?? group.description ?? t('cards.group.defaultName')
  }

  /** "HH:MM:SS" -> "HH:MM". */
  function timeShort(hhmmss: string): string {
    return hhmmss.slice(0, 5)
  }

  /**
   * One formatted line per schedule slot. WEEKLY (the only kind this app's
   * UI creates) renders as "<day> <start>–<end>"; MONTHLY_* slots — which
   * can only arrive from elsewhere, not from this app's create/edit form —
   * still render something readable instead of being silently dropped.
   */
  function scheduleLine(slot: GroupSchedule): string {
    const range = `${timeShort(slot.start_time)}–${timeShort(slot.end_time)}`
    switch (slot.recurrence_type) {
      case 'MONTHLY_BY_DAY':
        return slot.day_of_month != null
          ? t('cards.group.monthlyByDay', { day: slot.day_of_month, range })
          : range
      case 'MONTHLY_BY_WEEKDAY':
        return slot.day_of_week != null && slot.week_of_month != null
          ? t('cards.group.monthlyByWeekday', {
              week: slot.week_of_month,
              dow: dowLabels.value[slot.day_of_week],
              range,
            })
          : range
      case 'WEEKLY':
      default:
        return slot.day_of_week != null ? `${dowLabels.value[slot.day_of_week]} ${range}` : range
    }
  }

  function scheduleLines(schedule: GroupSchedule[] | null | undefined): string[] {
    return (schedule ?? []).map(scheduleLine)
  }

  return { dowLabels, displayName, timeShort, scheduleLine, scheduleLines }
}
